import { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, CheckCircle2, Mic, Users, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AppHeader } from "@/components/AppHeader";
import { PatientManager } from "@/components/PatientManager";
import { SessionView } from "@/components/SessionView";
import { DocumentsView } from "@/components/DocumentsView";
import { listAppointments, errMsg } from "@/api";
import { toast } from "sonner";

const Agenda = ({ onRecord }) => {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { setAppts(await listAppointments()); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  if (loading) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-brand-teal mx-auto" /></div>;

  return (
    <div>
      <h3 className="text-lg font-medium text-brand-navy capitalize mb-4">{today}</h3>
      {appts.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center">No hay citas para hoy. Usa la pestaña Pacientes para grabar.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="appointments-list">
          {appts.map((a) => (
            <div key={a.id} data-testid={`appointment-${a.id}`} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-sm"><Clock className="w-4 h-4" /> {a.time}</div>
                <p className="font-medium text-brand-navy text-lg">{a.patient_name}</p>
                <p className="text-sm text-slate-500">{a.reason}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {a.sent && <Badge className="bg-brand-teal/10 text-brand-teal border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Enviado</Badge>}
                  {a.has_note && !a.sent && <Badge className="bg-slate-100 text-slate-600 border-0">Nota guardada</Badge>}
                  {a.fragments_count > 0 && (
                    <Badge data-testid={`appointment-open-session-${a.id}`} className="bg-amber-100 text-amber-800 border-0">
                      {a.fragments_count} nota{a.fragments_count === 1 ? "" : "s"} sin finalizar
                    </Badge>
                  )}
                </div>
              </div>
              <Button data-testid={`record-appointment-${a.id}`} onClick={() => onRecord({ id: a.patient_id, name: a.patient_name, reason: a.reason }, a.id)} className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white h-11">
                <Mic className="w-4 h-4" /> {a.fragments_count > 0 ? "Continuar sesión" : "Grabar nota"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TherapistDashboard() {
  const [view, setView] = useState("home");
  const [patient, setPatient] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [docData, setDocData] = useState(null);
  const [audioDeleted, setAudioDeleted] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const startRecord = (p, aptId = null) => {
    setPatient(p);
    setAppointmentId(aptId);
    setView("record");
  };

  const onProcessed = (data) => {
    setDocData(data);
    setAppointmentId(data.appointment_id ?? appointmentId);
    setAudioDeleted(!!data.audio_deleted);
    setView("documents");
  };

  const backHome = () => {
    setView("home");
    setPatient(null);
    setAppointmentId(null);
    setDocData(null);
    setRefreshToken((t) => t + 1);
  };

  if (view === "record" && patient) {
    return (
      <div className="max-w-3xl mx-auto px-4">
        <SessionView patient={patient} appointmentId={appointmentId} onBack={backHome} onFinalized={onProcessed} />
      </div>
    );
  }

  if (view === "documents" && patient && docData) {
    return (
      <DocumentsView
        patient={patient}
        appointmentId={appointmentId}
        initial={docData}
        fragmentIds={docData.fragment_ids || []}
        audioDeleted={audioDeleted}
        onBack={backHome}
        onDone={backHome}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pb-24">
      <AppHeader />
      <div className="flex items-center gap-2 text-brand-teal text-xs font-bold uppercase tracking-[0.2em] mb-3">
        <Calendar className="w-4 h-4" /> Consulta
      </div>
      <Tabs defaultValue="agenda" className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="agenda" data-testid="tab-agenda"><Calendar className="w-4 h-4 mr-1" /> Agenda</TabsTrigger>
          <TabsTrigger value="pacientes" data-testid="tab-pacientes"><Users className="w-4 h-4 mr-1" /> Pacientes</TabsTrigger>
        </TabsList>
        <TabsContent value="agenda"><Agenda onRecord={startRecord} /></TabsContent>
        <TabsContent value="pacientes"><PatientManager onRecord={(p) => startRecord(p, null)} refreshToken={refreshToken} /></TabsContent>
      </Tabs>
    </div>
  );
}
